import { useParams, Link } from 'react-router-dom';
import NutritionLayout from "./NutritionLayout";
import ketoDiet from "../images/keto-diet.jpg";
import mediterraneanDiet from "../images/mediterranean-diet.jpg";
import veganDiet from "../images/vegan-diet.jpg";
import paleoDiet from "../images/paleo-diet.png";
import intermittentFasting from "../images/intermittent-fasting.png";
import dashDiet from "../images/dash-diet.jpg";

function NutritionDetails() {
  const { category } = useParams();

  // Dynamic image loader that tries multiple extensions
  const getImage = (imageName) => {
    const extensions = ['jpg', 'jpeg', 'png', 'webp'];
    
    for (const ext of extensions) {
      try {
        return require(`../images/meals/${imageName}.${ext}`);
      } catch (err) {
        // Continue to next extension
      }
    }
    
    // If none found, return fallback based on diet type
    const fallbackImages = {
      'keto': ketoDiet,
      'mediterranean': mediterraneanDiet,
      'vegan': veganDiet,
      'paleo': paleoDiet,
      'dash': dashDiet,
      'intermittent_fasting': intermittentFasting
    };
    
    return fallbackImages[category] || ketoDiet;
  };

  const nutritionData = {
    "keto": {
      name: "Keto Diet Plans",
      mealSets: [
        {
          setName: "Protein Focused",
          meals: [
            {
              name: "Scrambled Eggs & Avocado",
              image: getImage("scrambled-eggs-avocado"),
              description: "Fluffy scrambled eggs cooked slowly in grass-fed butter with half a ripe avocado and 2 strips of sugar-free bacon. The eggs provide high-quality protein and choline for brain health, while avocado delivers potassium and heart-healthy monounsaturated fats. Cook eggs on low heat, stirring frequently for creamy texture. Macros: 75% fat, 20% protein, 5% carbs. Perfect way to start your keto day with sustained energy and satiety.",
              mealType: "Breakfast"
            },
            {
              name: "Chicken Caesar Salad",
              image: getImage("chicken-caesar-salad"),
              description: "Grilled organic chicken breast (6oz) over crisp romaine lettuce with fresh parmesan cheese and homemade caesar dressing made with anchovies, olive oil, and lemon juice. Skip the croutons and add extra olive oil for healthy fats. Romaine provides folate and vitamin K, while chicken delivers complete proteins. Grill chicken with herbs like rosemary and thyme for extra flavor. Makes for a satisfying, restaurant-quality keto lunch.",
              mealType: "Lunch"
            },
            {
              name: "Salmon with Asparagus",
              image: getImage("salmon-asparagus"),
              description: "Wild-caught salmon fillet (6oz) grilled or baked with roasted asparagus spears cooked in butter, garlic, and fresh herbs. Salmon provides omega-3 fatty acids EPA and DHA for brain and heart health, while asparagus offers fiber and folate. Season salmon with lemon, dill, and black pepper. Roast asparagus at 400°F for 12-15 minutes until tender-crisp. This nutrient-dense dinner supports ketosis while providing anti-inflammatory benefits.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "Dairy-Free Focused",
          meals: [
            {
              name: "Coconut Flour Pancakes",
              image: getImage("coconut-flour-pancakes"),
              description: "Light and fluffy pancakes made with coconut flour, farm-fresh eggs, cream cheese, and a touch of vanilla extract. Topped with sugar-free maple syrup and a pat of grass-fed butter. Coconut flour is naturally grain-free and provides fiber and healthy fats. These pancakes have only 3g net carbs per serving. Cook on medium-low heat to prevent burning, as coconut flour browns quickly. A weekend keto treat that satisfies pancake cravings without breaking ketosis.",
              mealType: "Breakfast"
            },
            {
              name: "Tuna Salad Lettuce Wraps",
              image: getImage("tuna-salad-lettuce-wraps"),
              description: "Premium albacore tuna mixed with avocado mayo, diced celery, red onion, and fresh herbs like dill and parsley, wrapped in crisp butter lettuce leaves. Add capers and a squeeze of lemon for extra flavor. Tuna provides lean protein and selenium, while lettuce offers hydration and crunch. Use large Boston or butter lettuce leaves as wraps. This portable lunch is refreshing, light, and packed with protein and healthy fats - perfect for meal prep.",
              mealType: "Lunch"
            },
            {
              name: "Beef Stir-Fry",
              image: getImage("beef-stir-fry"),
              description: "Tender grass-fed beef strips stir-fried with broccoli florets, colorful bell peppers, and onions in coconut oil over high heat. Seasoned with fresh ginger, garlic, and coconut aminos (soy sauce substitute). The key is to cook quickly over high heat to maintain vegetable crispness and beef tenderness. Beef provides iron and B-vitamins, while vegetables add fiber and antioxidants. Serve over cauliflower rice for a complete Asian-inspired keto dinner.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "Vegetarian Focused",
          meals: [
            {
              name: "Chia Seed Pudding",
              image: getImage("chia-seed-pudding"),
              description: "Overnight chia seed pudding made with rich coconut milk, pure vanilla extract, and topped with crushed almonds and a few fresh berries. Chia seeds expand overnight, creating a pudding-like texture while providing omega-3 fatty acids, fiber, and complete proteins. Sweeten naturally with stevia or monk fruit. The pudding thickens after 4+ hours in the refrigerator. This make-ahead breakfast provides sustained energy and supports digestive health while keeping carbs minimal.",
              mealType: "Breakfast"
            },
            {
              name: "Stuffed Portobello Mushrooms",
              image: getImage("stuffed-portobello-mushrooms"),
              description: "Large portobello mushroom caps stuffed with sautéed spinach, creamy feta cheese, sun-dried tomatoes, and fresh herbs like basil and oregano. Drizzle with extra virgin olive oil and bake until tender. Remove mushroom stems and scrape out gills before stuffing. Portobellos provide umami flavor and meaty texture, while spinach offers iron and folate. These savory stuffed mushrooms make an elegant, restaurant-quality keto lunch that's both satisfying and nutrient-dense.",
              mealType: "Lunch"
            },
            {
              name: "Zucchini Noodles with Pesto",
              image: getImage("zucchini-noodles-pesto"),
              description: "Fresh zucchini spiralized into noodles and tossed with homemade basil pesto made from fresh basil, pine nuts, garlic, parmesan cheese, and olive oil. Lightly sauté zucchini noodles for 2-3 minutes to warm through without making them soggy. Pesto provides healthy fats and fresh herb antioxidants. Use a spiralizer or vegetable peeler to create noodles. This Italian-inspired dish proves that keto can be both delicious and satisfying - a perfect pasta alternative.",
              mealType: "Dinner"
            }
          ]
        }
      ]
    },
    "mediterranean": {
      name: "Mediterranean Diet Plans",
      mealSets: [
        {
          setName: "Traditional Mediterranean",
          meals: [
            {
              name: "Greek Yogurt Bowl",
              image: getImage("greek-yogurt-bowl"),
              description: "Thick, creamy Greek yogurt (2% fat) topped with golden honey, crunchy walnuts, and a medley of fresh berries including blueberries, strawberries, and blackberries. Greek yogurt provides probiotics for gut health and double the protein of regular yogurt. The berries offer antioxidants and natural sweetness, while walnuts contribute omega-3 fatty acids and vitamin E. Drizzle with local honey for natural energy. This protein-rich breakfast provides sustained energy and supports digestive health.",
              mealType: "Breakfast"
            },
            {
              name: "Greek Salad",
              image: getImage("greek-salad"),
              description: "Traditional village-style Greek salad with ripe tomatoes, crisp cucumbers, red onion, Kalamata olives, and authentic feta cheese, dressed with extra virgin olive oil, red wine vinegar, and dried oregano. No lettuce needed - this is the authentic way! Tomatoes provide lycopene, cucumbers offer hydration, and olives deliver healthy monounsaturated fats. Use the best quality olive oil you can find. Fresh, light, and bursting with Mediterranean flavors that transport you to the Greek islands.",
              mealType: "Lunch"
            },
            {
              name: "Grilled Fish with Quinoa",
              image: getImage("grilled-fish-quinoa"),
              description: "Wild-caught white fish (such as sea bass or branzino) grilled with lemon, olive oil, and Mediterranean herbs, served with fluffy quinoa pilaf cooked in vegetable broth and roasted vegetables including zucchini, bell peppers, and red onion. Fish provides lean protein and omega-3s, while quinoa offers complete proteins and fiber. Finish with fresh herbs like parsley and dill, plus a drizzle of extra virgin olive oil. This heart-healthy dinner embodies the Mediterranean lifestyle.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "Coastal Mediterranean",
          meals: [
            {
              name: "Avocado Toast",
              image: getImage("avocado-toast"),
              description: "Thick slices of whole grain sourdough bread topped with perfectly ripe, mashed avocado seasoned with sea salt, black pepper, and a drizzle of premium extra virgin olive oil. Garnish with halved cherry tomatoes and a sprinkle of red pepper flakes. Avocados provide heart-healthy monounsaturated fats, fiber, and potassium. Choose bread with visible grains and seeds for maximum nutrition. This simple yet satisfying breakfast combines healthy fats with complex carbohydrates for sustained energy.",
              mealType: "Breakfast"
            },
            {
              name: "Lentil Soup",
              image: getImage("lentil-soup"),
              description: "Hearty Mediterranean lentil soup made with green or brown lentils, diced tomatoes, carrots, celery, onions, and garlic, simmered in vegetable broth with bay leaves, thyme, and oregano. Finish with a generous drizzle of extra virgin olive oil and fresh lemon juice. Served with crusty whole grain bread for dipping. Lentils provide plant-based protein, fiber, and folate. This comforting soup is both nutritious and filling, perfect for cooler days and meal prep.",
              mealType: "Lunch"
            },
            {
              name: "Seafood Paella",
              image: getImage("seafood-paella"),
              description: "Traditional Spanish paella with saffron-infused bomba rice, fresh shrimp, mussels, clams, and sometimes squid, cooked with tomatoes, garlic, and Spanish paprika in a wide paella pan. Saffron gives the distinctive golden color and aromatic flavor. Seafood provides lean protein and omega-3s, while rice offers energy-sustaining carbohydrates. Cook in a wide, shallow pan for the best socarrat (crispy bottom layer). A festive dinner that brings the coastal flavors of Spain to your table.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "Garden Fresh Mediterranean",
          meals: [
            {
              name: "Berry Smoothie Bowl",
              image: getImage("berry-smoothie-bowl"),
              description: "A thick, spoonable smoothie bowl made from blended mixed berries (blueberries, strawberries, raspberries) and Greek yogurt, topped with crunchy granola, sliced almonds, and a drizzle of honey. The berries deliver antioxidants and vitamin C, while Greek yogurt adds protein and probiotics. Customize with chia seeds or coconut flakes for extra texture. It’s a refreshing, filling breakfast that feels like a treat but fits right into a Mediterranean-style, plant-forward day.",
              mealType: "Breakfast"
            },
            {
              name: "Hummus Vegetable Wrap",
              image: getImage("hummus-vegetable-wrap"),
              description: "A whole wheat or spinach wrap generously spread with creamy hummus and piled with crisp lettuce, cucumber, tomato, red onion, and fresh herbs like parsley and mint. Optional add-ins include roasted red pepper, olives, or a squeeze of lemon. Hummus provides plant-based protein and fiber from chickpeas and tahini, while the vegetables add vitamins and crunch. Easy to pack and eat on the go, this wrap is a staple Mediterranean lunch that keeps you full without feeling heavy.",
              mealType: "Lunch"
            },
            {
              name: "Herb-Crusted Chicken",
              image: getImage("herb-crusted-chicken"),
              description: "Tender chicken breast coated in a crust of Mediterranean herbs—oregano, thyme, rosemary, and garlic—then grilled or baked until golden and juicy. Served alongside roasted eggplant wedges and a spoonful of olive tapenade for a punch of briny, olive-rich flavor. The herbs add aroma and antioxidants, while the olive oil in the tapenade contributes heart-healthy monounsaturated fats. Light, satisfying, and deeply rooted in coastal Mediterranean cooking.",
              mealType: "Dinner"
            }
          ]
        }
      ]
    },
    "vegan": {
      name: "Vegan Diet Plans",
      mealSets: [
        {
          setName: "Protein Power",
          meals: [
            {
              name: "Tofu Scramble",
              image: getImage("tofu-scramble"),
              description: "Crumbled firm tofu sautéed with nutritional yeast, turmeric (for golden color), garlic powder, and a medley of colorful vegetables including bell peppers, spinach, and mushrooms. Season with black pepper and a pinch of kala namak (black salt) for an eggy flavor. Nutritional yeast provides B-vitamins and a cheesy taste, while tofu offers complete plant-based protein. Cook in a non-stick pan with a little olive oil. This protein-rich breakfast proves that plant-based eating can be both satisfying and delicious.",
              mealType: "Breakfast"
            },
            {
              name: "Quinoa Buddha Bowl",
              image: getImage("quinoa-buddha-bowl"),
              description: "Nourishing bowl with fluffy quinoa as the base, topped with roasted rainbow vegetables (sweet potato, broccoli, red cabbage), protein-rich chickpeas, sliced avocado, and a creamy tahini dressing made with sesame paste, lemon juice, and garlic. Sprinkle with hemp seeds and fresh herbs. Quinoa provides all nine essential amino acids, making it a complete protein. This colorful bowl delivers a wide range of nutrients, healthy fats, and satisfying plant-based proteins in every bite.",
              mealType: "Lunch"
            },
            {
              name: "Lentil Black Bean Chili",
              image: getImage("lentil-black-bean-chili"),
              description: "Hearty, protein-packed chili made with red lentils, black beans, diced tomatoes, bell peppers, onions, and warming spices including cumin, chili powder, and smoked paprika. Slow-simmered to develop rich flavors and topped with fresh cilantro, diced avocado, and a squeeze of lime. Lentils and beans provide complementary proteins, fiber, and iron. This satisfying dinner is perfect for meal prep and gets even better the next day as flavors meld together.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "Whole Foods Focused",
          meals: [
            {
              name: "Oatmeal with Almond Butter",
              image: getImage("oatmeal-almond-butter"),
              description: "Steel-cut oats cooked until creamy and topped with a generous swirl of almond butter, sliced banana, and a sprinkle of chia seeds. Oats provide soluble fiber and slow-release carbohydrates for steady energy, while almond butter adds healthy fats and a bit of protein. Chia seeds contribute omega-3s and extra fiber. Sweet, comforting, and entirely plant-based—a classic whole-foods breakfast that keeps you satisfied through the morning.",
              mealType: "Breakfast"
            },
            {
              name: "Chickpea Salad Sandwich",
              image: getImage("chickpea-salad-sandwich"),
              description: "Mashed chickpeas mixed with diced celery, red onion, fresh dill or parsley, and a creamy vegan mayo (or tahini) dressing, served on hearty whole grain bread with lettuce and tomato. Chickpeas deliver plant-based protein and fiber, making this sandwich filling and nutritious. Add a squeeze of lemon or a pinch of turmeric for extra flavor. It’s a portable, protein-packed lunch that’s perfect for meal prep and tastes even better the next day.",
              mealType: "Lunch"
            },
            {
              name: "Stuffed Bell Peppers",
              image: getImage("stuffed-bell-peppers"),
              description: "Colorful bell peppers hollowed out and stuffed with a mixture of cooked brown rice, black beans, corn, diced tomatoes, onions, and herbs, then baked until the peppers are tender and the filling is golden. A sprinkle of nutritional yeast adds a cheesy, savory note without any dairy. The combination of whole grains, legumes, and vegetables provides fiber, protein, and a range of vitamins. A satisfying, family-friendly vegan dinner that’s easy to batch-cook and reheat.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "International Flavors",
          meals: [
            {
              name: "Chia Pudding Parfait",
              image: getImage("chia-pudding-parfait"),
              description: "Creamy chia seed pudding made with full-fat coconut milk and a touch of vanilla, layered in a glass with ripe mango cubes and toasted coconut flakes. Chia seeds swell overnight into a pudding-like texture and provide omega-3 fatty acids, fiber, and a small amount of plant-based protein. The mango adds natural sweetness and vitamin C. Make it the night before for a no-fuss, tropical-inspired breakfast that feels indulgent but is entirely whole-foods and vegan.",
              mealType: "Breakfast"
            },
            {
              name: "Thai Curry with Tofu",
              image: getImage("thai-curry-tofu"),
              description: "Firm tofu cubes simmered in a fragrant red curry paste and coconut milk with bell peppers, bamboo shoots, and Thai basil, served over steamed brown rice. The curry is rich, slightly spicy, and full of depth from ginger, garlic, and lemongrass. Tofu soaks up the sauce and adds plant-based protein, while coconut milk contributes healthy fats and creaminess. A satisfying, restaurant-style vegan lunch that’s easy to scale up for meal prep.",
              mealType: "Lunch"
            },
            {
              name: "Pasta with Cashew Cream",
              image: getImage("pasta-cashew-cream"),
              description: "Whole wheat or legume-based pasta tossed with a velvety cashew cream sauce—blended soaked cashews with garlic, nutritional yeast, and a splash of plant milk—topped with marinara and fresh basil. The cashew cream is rich and creamy without any dairy, and pairs beautifully with the acidity of the tomato sauce. Add sautéed mushrooms or spinach for extra heft. A comforting, Italian-inspired vegan dinner that proves you don’t need cheese or cream for a decadent pasta night.",
              mealType: "Dinner"
            }
          ]
        }
      ]
    },
    "paleo": {
      name: "Paleo Diet Plans",
      mealSets: [
        {
          setName: "Hunter-Gatherer",
          meals: [
            {
              name: "Sweet Potato Hash",
              image: getImage("sweet-potato-hash"),
              description: "Diced sweet potatoes roasted until crispy and golden, combined with farm-fresh eggs, colorful bell peppers, and caramelized onions, all cooked in coconut oil with fresh herbs like thyme and rosemary. Sweet potatoes provide complex carbohydrates, beta-carotene, and potassium, while eggs offer complete proteins and healthy fats. Cook sweet potatoes first until tender, then add other ingredients. This hearty breakfast follows paleo principles while providing sustained energy for active mornings.",
              mealType: "Breakfast"
            },
            {
              name: "Grilled Chicken Salad",
              image: getImage("grilled-chicken-salad"),
              description: "Organic, free-range chicken breast marinated in olive oil, lemon, and herbs, then grilled to perfection and served over a bed of mixed organic greens, sliced avocado, toasted nuts (almonds or walnuts), and cherry tomatoes. Dressed with a simple olive oil and balsamic vinegar dressing. Chicken provides lean protein and B-vitamins, while the variety of vegetables and healthy fats create a balanced, nutrient-dense meal that's completely paleo-compliant and satisfying.",
              mealType: "Lunch"
            },
            {
              name: "Grass-Fed Beef & Vegetables",
              image: getImage("grass-fed-beef-vegetables"),
              description: "Premium grass-fed beef (sirloin or ribeye) grilled or pan-seared to your preference, served with a medley of roasted root vegetables including carrots, parsnips, turnips, and Brussels sprouts, all seasoned with fresh herbs like rosemary and thyme. Grass-fed beef provides higher omega-3 fatty acids and CLA compared to grain-fed beef. Root vegetables offer natural sweetness and essential nutrients. This satisfying dinner provides high-quality protein and nutrients our ancestors would recognize.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "Seafood Focused",
          meals: [
            {
              name: "Smoked Salmon & Avocado",
              image: getImage("smoked-salmon-avocado"),
              description: "Wild-caught smoked salmon (lox-style or hot-smoked) served with thick slices of ripe avocado and a bed of mixed greens—arugula, spinach, or butter lettuce—dressed with a squeeze of lemon and a drizzle of olive oil. Salmon provides omega-3 fatty acids and high-quality protein, while avocado adds heart-healthy monounsaturated fats and creaminess. No grains or dairy, just clean paleo fats and protein. A quick, elegant breakfast or brunch that keeps you full and focused.",
              mealType: "Breakfast"
            },
            {
              name: "Shrimp Avocado Salad",
              image: getImage("shrimp-avocado-salad"),
              description: "Succulent grilled or pan-seared shrimp tossed with diced avocado, cucumber, cherry tomatoes, and fresh herbs—cilantro, dill, or parsley—in a simple lemon and olive oil vinaigrette. The shrimp offer lean protein and minerals like selenium; avocado adds healthy fats and a creamy contrast. Light, refreshing, and perfect for warm weather. Serve on a large bed of greens for a complete paleo lunch that’s satisfying without feeling heavy.",
              mealType: "Lunch"
            },
            {
              name: "Baked Cod with Sweet Potato",
              image: getImage("baked-cod-sweet-potato"),
              description: "Mild, flaky wild-caught cod fillet baked with lemon, herbs, and a touch of olive oil, alongside roasted sweet potato wedges and steamed broccoli. Cod is low in fat but high in protein and B vitamins; sweet potatoes provide complex carbs and beta-carotene without grains. The dish is simple to prepare, easy to digest, and fits paleo guidelines perfectly. A light yet filling dinner that supports recovery and steady energy.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "Plant-Heavy",
          meals: [
            {
              name: "Coconut Flour Muffins",
              image: getImage("coconut-flour-muffins"),
              description: "Tender, grain-free muffins made with coconut flour, eggs, and a handful of fresh blueberries or raspberries, sweetened only with a bit of ripe banana or a touch of honey. Coconut flour is high in fiber and healthy fats and keeps carbs low. Bake a batch ahead for grab-and-go paleo breakfasts or snacks. They’re satisfying, portable, and free of grains, dairy, and refined sugar—ideal when you want something baked without breaking paleo rules.",
              mealType: "Breakfast"
            },
            {
              name: "Turkey & Vegetable Salad",
              image: getImage("turkey-vegetable-salad"),
              description: "Sliced roasted or grilled turkey breast served over a large mixed salad of leafy greens, cucumber, tomato, bell pepper, and red onion, dressed with extra virgin olive oil, lemon juice, and herbs. Turkey provides lean protein and B vitamins with minimal fat; the vegetables add fiber, vitamins, and crunch. No grains or legumes—just clean protein and plenty of plants. A simple, filling paleo lunch that’s easy to prep in bulk.",
              mealType: "Lunch"
            },
            {
              name: "Zucchini Noodles with Meat Sauce",
              image: getImage("zucchini-noodles-meat-sauce"),
              description: "Spiralized zucchini noodles (zoodles) topped with a rich, savory meat sauce made from grass-fed ground beef, tomatoes, garlic, onion, and Italian herbs—no sugar or grains. The zucchini softens just enough when briefly sautéed or served with the hot sauce, keeping a slight bite. It’s a paleo-friendly spin on spaghetti and meat sauce: comforting, hearty, and full of protein and vegetables without any pasta. Family-friendly and great for batch cooking.",
              mealType: "Dinner"
            }
          ]
        }
      ]
    },
    "dash": {
      name: "DASH Diet Plans",
      mealSets: [
        {
          setName: "Heart Healthy",
          meals: [
            {
              name: "Oatmeal & Banana",
              image: getImage("oatmeal-banana"),
              description: "Steel-cut oats cooked with low-fat milk (or a plant-based alternative) and topped with sliced banana and optional cinnamon or nuts. Oats deliver soluble fiber that can help support healthy cholesterol levels, while banana adds natural sweetness and potassium—a key mineral for blood pressure. Keep added salt to a minimum to stay within DASH guidelines. A warm, filling breakfast that’s simple to make and aligns with heart-healthy, low-sodium eating.",
              mealType: "Breakfast"
            },
            {
              name: "Turkey Vegetable Soup",
              image: getImage("turkey-vegetable-soup"),
              description: "A comforting soup made with lean turkey, plenty of vegetables—carrots, celery, onions, tomatoes, and greens—and low-sodium broth, served with a small whole grain roll for dipping. The turkey provides lean protein without excess saturated fat; the vegetables add fiber, vitamins, and volume. Use herbs and a squeeze of lemon instead of heavy salt to keep sodium in check. Hearty, satisfying, and perfect for meal prep or a cozy DASH-friendly lunch.",
              mealType: "Lunch"
            },
            {
              name: "Baked Chicken & Rice",
              image: getImage("baked-chicken-rice"),
              description: "Herb-baked chicken breast—seasoned with rosemary, thyme, and garlic instead of salt—served with fluffy brown rice and steamed vegetables like broccoli or green beans. Chicken offers lean protein; brown rice provides whole grains and fiber. Bake the chicken without added salt and use a minimal-sodium broth for the rice if desired. A balanced, comforting DASH dinner that supports heart health and blood pressure goals.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "Blood Pressure Control",
          meals: [
            {
              name: "Greek Yogurt Parfait",
              image: getImage("greek-yogurt-parfait"),
              description: "Low-fat Greek yogurt layered in a glass with fresh berries (blueberries, strawberries, raspberries) and a sprinkle of unsalted nuts or seeds—walnuts, almonds, or pumpkin seeds. Greek yogurt delivers protein and calcium with minimal sodium; berries add antioxidants and natural sweetness; nuts contribute healthy fats and a bit of crunch. Skip added salt and sweeten lightly with honey or fruit only. A quick, DASH-friendly breakfast that supports blood pressure and keeps you full.",
              mealType: "Breakfast"
            },
            {
              name: "Quinoa Vegetable Salad",
              image: getImage("quinoa-vegetable-salad"),
              description: "Fluffy cooked quinoa tossed with colorful raw or lightly cooked vegetables—cucumber, cherry tomatoes, bell pepper, red onion, and herbs—plus optional grilled chicken or chickpeas, dressed with olive oil, lemon juice, and minimal or no salt. Quinoa provides complete protein and magnesium; vegetables add fiber and potassium. The combination supports cardiovascular health and fits DASH’s emphasis on whole grains, vegetables, and low sodium. Perfect for lunch or as a make-ahead side.",
              mealType: "Lunch"
            },
            {
              name: "Grilled Salmon & Quinoa",
              image: getImage("grilled-salmon-quinoa"),
              description: "Wild-caught salmon fillet (4-6oz) grilled with lemon and herbs, served with fluffy quinoa pilaf and roasted Brussels sprouts drizzled with olive oil and a touch of balsamic vinegar. Salmon provides omega-3 fatty acids EPA and DHA for heart and brain health, while quinoa offers complete proteins and fiber. Brussels sprouts are rich in vitamin K and antioxidants. This meal is naturally low in sodium while being rich in potassium and magnesium - key minerals for blood pressure management and optimal cardiovascular health.",
              mealType: "Dinner"
            }
          ]
        },
        {
          setName: "Balanced Approach",
          meals: [
            {
              name: "Whole Grain Toast",
              image: getImage("whole-grain-toast"),
              description: "Two slices of 100% whole grain toast spread with natural almond butter (no added salt or sugar) and topped with banana slices. Whole grains provide fiber and sustained energy; almond butter adds healthy fats and a bit of protein; banana contributes potassium, which supports healthy blood pressure. Simple, satisfying, and DASH-friendly—especially when you choose low-sodium bread and avoid sprinkling extra salt.",
              mealType: "Breakfast"
            },
            {
              name: "Lentil Vegetable Salad",
              image: getImage("lentil-vegetable-salad"),
              description: "Cooked green or brown lentils tossed with diced tomatoes, cucumber, red onion, fresh parsley, and a light dressing of olive oil and lemon juice—with little or no added salt. Lentils are rich in fiber, plant-based protein, potassium, and magnesium, all of which support cardiovascular health and blood pressure management. The salad is filling, versatile, and ideal for batch prep. Serve alone or with a small whole grain roll for a complete DASH lunch.",
              mealType: "Lunch"
            },
            {
              name: "Lean Beef & Sweet Potato",
              image: getImage("lean-beef-sweet-potato"),
              description: "A modest portion of lean beef (such as sirloin or round), grilled or pan-seared with herbs and minimal salt, served with a baked sweet potato and steamed green beans. Lean beef provides protein and iron; sweet potato adds fiber, potassium, and beta-carotene; green beans round out the plate with vitamins and crunch. Keep the beef portion to about 3–4 oz and season with pepper and herbs rather than salt. A balanced, satisfying DASH dinner that supports heart and blood pressure goals.",
              mealType: "Dinner"
            }
          ]
        }
      ]
    },
    "intermittent_fasting": {
      name: "Intermittent Fasting Guide",
      mealSets: [
        {
          setName: "16:8 Method (Most Popular)",
          meals: [
            {
              name: "16:8 Method Guide",
              image: getImage("16-8-method"),
              description: "You fast for 16 hours and eat all your meals within an 8-hour window each day. A common schedule is eating between 12pm and 8pm, then fasting from 8pm until noon the next day—so you skip breakfast and have lunch as your first meal. During the fasting window, stick to water, black coffee, or unsweetened tea; avoid anything that spikes insulin. Many people find this approach easy to maintain because most of the fast happens while you sleep. Start with a 12–14 hour overnight fast and gradually extend to 16 hours as you get comfortable.",
              mealType: "Method"
            }
          ]
        },
        {
          setName: "5:2 Method (Flexible)",
          meals: [
            {
              name: "5:2 Method Guide",
              image: getImage("5-2-method"),
              description: "You eat normally five days of the week and restrict calories to about 500 (women) or 600 (men) on two non-consecutive “fasting” days—for example, Monday and Thursday. On fasting days, focus on light, nutrient-dense meals: vegetable soup, salads, lean protein like chicken or fish, and plenty of non-starchy vegetables. On the other five days, eat a balanced diet without strict limits. This method is flexible and can fit around social plans, since you only need to plan for two lower-calorie days. Good for those who prefer not to skip entire meals or long windows.",
              mealType: "Method"
            }
          ]
        },
        {
          setName: "Eat-Stop-Eat (Advanced)",
          meals: [
            {
              name: "Eat-Stop-Eat Guide",
              image: getImage("eat-stop-eat"),
              description: "You do a full 24-hour fast once or twice per week. For example, finish dinner at 7pm Tuesday and don’t eat again until 7pm Wednesday. During the fast, only water, black coffee, and plain tea are allowed—no calories. When you break the fast, do it gently with a normal-sized meal rather than a huge feast to avoid discomfort. This approach is more demanding than daily time-restricted eating and is better suited to people who already have experience with shorter fasts. Not recommended for beginners or anyone with a history of disordered eating.",
              mealType: "Method"
            }
          ]
        },
        {
          setName: "OMAD - One Meal A Day (Expert)",
          meals: [
            {
              name: "OMAD Guide",
              image: getImage("omad-method"),
              description: "You eat one large, nutritionally complete meal per day within a 1-hour window (e.g., 6–7pm) and fast the remaining 23 hours. Because you have only one meal to hit your needs, planning matters: aim for plenty of protein, healthy fats, vegetables, and some complex carbohydrates so you get enough calories, fiber, and micronutrients. Many people find it simplest to cook a big dinner that includes a protein source, vegetables, and a carb like rice or sweet potato. OMAD is advanced and can be difficult socially and physically; it’s best for those who’ve already adapted to shorter daily fasts and have no medical contraindications.",
              mealType: "Method"
            }
          ]
        }
      ]
    }
  };

  const currentNutrition = nutritionData[category];

  if (!currentNutrition) {
    return (
      <div className="page-container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
        <h1 className="text-2xl font-bold text-white">Nutrition Plan Not Found</h1>
        <Link to="/Nutrition" className="mt-4 font-medium text-blue-400 hover:text-blue-300">
          ← Back to Nutrition Plans
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container py-10 sm:py-12">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="page-heading mb-0 border-0 pb-0">{currentNutrition.name}</h1>
        <Link
          to="/Nutrition"
          className="text-sm font-medium text-neutral-400 hover:text-white transition-colors shrink-0"
        >
          ← Back to Nutrition Plans
        </Link>
      </div>
      <div className="space-y-12 pb-16">
        {currentNutrition.mealSets.map((mealSet, setIndex) => (
          <section key={setIndex}>
            <h2 className="text-lg font-semibold text-neutral-200 mb-4">{mealSet.setName}</h2>
            <div className="space-y-8">
              {mealSet.meals.map((meal, mealIndex) => (
                <NutritionLayout
                  key={mealIndex}
                  name={`${meal.mealType}: ${meal.name}`}
                  image={meal.image}
                  description={meal.description}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default NutritionDetails;